/**
 * NOS-113: Path selection for onboarding — "Start Fresh" vs "Clone My Site"
 */
import { Button } from "@/components/ui/button"
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { FileText, Globe } from "lucide-react"

export type OnboardingPath = "fresh" | "clone"

interface PathSelectorProps {
  onSelect: (path: OnboardingPath) => void
  onBack: () => void
  disabled?: boolean
}

export function PathSelector({ onSelect, onBack, disabled }: PathSelectorProps) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Card
        className="cursor-pointer transition-colors hover:border-primary/50 hover:bg-muted/30"
        onClick={() => !disabled && onSelect("fresh")}
      >
        <CardHeader className="space-y-1">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <CardTitle className="text-lg">Start Fresh</CardTitle>
          <CardDescription>
            Answer a few questions and we&apos;ll generate your website copy.
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={(e) => {
              e.stopPropagation()
              if (!disabled) onSelect("fresh")
            }}
            disabled={disabled}
          >
            Choose this path
          </Button>
        </CardFooter>
      </Card>

      <Card
        className="cursor-pointer transition-colors hover:border-primary/50 hover:bg-muted/30"
        onClick={() => !disabled && onSelect("clone")}
      >
        <CardHeader className="space-y-1">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Globe className="h-5 w-5 text-primary" />
          </div>
          <CardTitle className="text-lg">Clone My Site</CardTitle>
          <CardDescription>
            Paste your current website URL and we&apos;ll import your content.
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={(e) => {
              e.stopPropagation()
              if (!disabled) onSelect("clone")
            }}
            disabled={disabled}
          >
            Choose this path
          </Button>
        </CardFooter>
      </Card>

      <div className="sm:col-span-2">
        <Button type="button" variant="ghost" onClick={onBack} disabled={disabled}>
          Back
        </Button>
      </div>
    </div>
  )
}
